import { Helmet } from 'react-helmet-async';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

// @mui
import {
	Box,
	Stack,
	Tooltip,
	Button,
	Popover,
	MenuItem,
	IconButton,
	Dialog,
	NativeSelect,
	Typography,
	Grid,
} from '@mui/material';
import { DataGrid, Toolbar } from '@mui/x-data-grid';

// components
import Iconify from '../../../components/iconify';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../../components/datagrid';
import { BulkRequestListSkeleton } from '../../../components/shared';
import { Status, PaymentStatus } from '../../../components/request';
import { BulkRequest, Request, type BulkRequestProps, type Status as RequestStatus, type TestProps } from '../../../models';
import { useRole } from '../../../hooks/useRole';

import { useGetBulkRequestsQuery, useUpdateBulkRequestStatusMutation } from '../../../slices/apis/bulkRequestApi';

import { fDateTime } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

type DataGridRow = {
	id: BulkRequestProps['id'];
	company: string;
	testingLocation: string;
	tests: TestProps[];
	assessmentDates: Date[];
	paymentStatus: BulkRequestProps['paymentStatus'];
	status: BulkRequestProps['status'];
};

const findRequest = (requests: BulkRequestProps[], id: number) => {
	return requests.find((request) => Number(request.id) === id);
};

export default function UserContractorView() {
	const { isContractor } = useRole();
	const { data = [], isFetching } = useGetBulkRequestsQuery();
	const theme = useTheme();

	const requests = data as BulkRequestProps[];

	const [, setRequestEditable] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const navigate = useNavigate();

	/*
		request deletion
	*/
	const [selectedRequest, setSelectedRequest] = useState<BulkRequestProps | null>(null);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		const requestId = Number(event.currentTarget.getAttribute('data-request-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			setAnchorEl(event.currentTarget);
			setSelectedRequest(request);
			setRequestEditable(true);
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/bulk-requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/bulk-requests/${selectedRequest.id}/view`);
		}
	};

	/*
		del request
	*/
	const handleDeleteRequest = () => {
		setAnchorEl(null);
	};

	/*
		request status editing (CONTRACTOR only)
	*/
	const [statusEditingDialogOpen, setStatusEditingDialogOpen] = useState<boolean>(false);
	const [updateRequestStatus, { isSuccess: isUpdateReqStatusSuccess, isLoading: isUpdateReqStatusLoading }] =
		useUpdateBulkRequestStatusMutation();

	const statusOptions: RequestStatus[] = isContractor ? Request.getAllowedEditableStatusContractor() : [];

	const handleOpenStatusEditingDialog = (e: React.MouseEvent<HTMLDivElement>) => {
		const requestId = Number(e.currentTarget.getAttribute('data-correlation-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			if (Request.isStatusEditingAllowed(request.status)) {
				setStatusEditingDialogOpen(true);
				setSelectedRequest(request);
			}
		}
	};

	const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!statusOptions.includes(e.target.value as RequestStatus)) {
			return;
		}
		setSelectedRequest({
			...selectedRequest,
			status: e.target.value,
		} as BulkRequestProps);
	};

	const onStatusUpdate = () => {
		if (selectedRequest) {
			const { id, status } = selectedRequest;
			updateRequestStatus({
				id,
				status,
			});
		}
	};

	const handleCloseStatusEditingDialog = () => {
		setStatusEditingDialogOpen(false);
	};

	useEffect(() => {
		if (isUpdateReqStatusSuccess) {
			setStatusEditingDialogOpen(false);
			navigate('/dashboard/bulk-requests');
		}
	}, [isUpdateReqStatusSuccess, navigate]);

	return (
		<>
			<Helmet>
				<title> Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
				{!isContractor && (
					<Stack spacing={4} mb={4}>
						{!isFetching && (
							<Box>
								<Button
									variant="contained"
									color="secondary"
									data-route="/dashboard/bulk-requests/new"
									onClick={handleNavigate}
									startIcon={<Iconify icon="eva:plus-fill" />}
								>
									New Bulk Request
								</Button>
							</Box>
						)}
					</Stack>
				)}

				{isFetching ? (
					<BulkRequestListSkeleton />
				) : (
					<>
						<DataGridContainer>
							<DataGrid
								disableColumnFilter
								disableDensitySelector
								disableColumnSelector
								showToolbar
								columnHeaderHeight={48}
								getRowHeight={({ model }) => {
									const rowData = model as DataGridRow;
									const testCount = rowData?.tests?.length ?? 1;
									const baseHeight = 64;
									const extraPerTest = 20;
									return Math.max(baseHeight, baseHeight + Math.max(0, testCount - 1) * extraPerTest);
								}}
								slots={{
									toolbar: () => (
										<Toolbar>
											<Box sx={{ pl: 1 }}>
												<ToolBarSearch />
											</Box>
											<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Tooltip title="Columns">
													<ToolBarColumnToggler />
												</Tooltip>
											</Box>
										</Toolbar>
									),
								}}
								sx={{
									width: '100%',
									borderRadius: 3,
									'& .MuiDataGrid-columnHeaderTitle': {
										whiteSpace: 'normal',
										lineHeight: 1.4,
										fontSize: '0.9rem',
										color: 'grey',
									},
									'& .MuiDataGrid-columnHeader': {
										backgroundColor: theme.palette.grey[100],
									},
								}}
								columns={[
									{
										field: 'id',
										headerName: 'ID',
										resizable: false,
										sortable: false,
										flex: 0.25,
									},
									{
										field: 'tests',
										headerName: 'Tests',
										sortable: false,
										flex: 1.5,
										valueGetter: (_value, row) => {
											return row.tests.map((test) => {
												return test.name;
											});
										},
										renderCell: (params) => {
											const {
												row: { tests },
											} = params;

											return (
												<Stack alignItems="left" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
													{tests.map((test) => (
														<Typography key={test.name} variant="body2">
															{`${test.quantity} x ${test.name}`}
														</Typography>
													))}
												</Stack>
											);
										},
									},
									{
										field: 'testingLocation',
										headerName: 'Test Location',
										sortable: false,
										flex: 2,
									},
									{
										field: 'assessmentDates',
										headerName: 'Asssessment Dates',
										flex: 1,
										valueGetter: (_value, row) => {
											return BulkRequest.isAssessmentDatesSet(row.assessmentDates)
												? row.assessmentDates.map((date: Date) => fDateTime(date)).join(' ')
												: 'Pending';
										},
										renderCell: (params) => {
											const {
												row: { id, assessmentDates },
											} = params;
											const sx = {
												display: 'flex',
												alignItems: 'center',
												height: '100%',
												width: '100%',
												cursor: 'pointer',
												'&:focus': {
													cursor: 'pointer',
												},
												'&:focus-visible': {
													cursor: 'pointer',
												},
											};

											if (BulkRequest.isAssessmentDatesSet(assessmentDates)) {
												return (
													<Stack alignItems="center" justifyContent="center" sx={{ height: '100%', width: '100%' }}>
														{assessmentDates.map((date) => (
															<Typography
																key={`${id}-${new Date(date).getTime()}`}
																color="primary"
																variant="subtitle2"
																data-request-id={id}
																sx={sx}
															>
																{fDateTime(date)}
															</Typography>
														))}
													</Stack>
												);
											}

											return (
												<Typography color="primary" variant="subtitle2" data-request-id={id} sx={sx}>
													Pending
												</Typography>
											);
										},
									},
									{
										field: 'paymentStatus',
										headerName: 'Payment Status',
										resizable: false,
										flex: 0.9,
										renderCell: (params) => {
											const { row } = params;
											return <PaymentStatus paymentStatus={row.paymentStatus || 'Unknown'} />;
										},
									},
									{
										field: 'status',
										headerName: 'Request Status',
										resizable: false,
										flex: 1,
										renderCell: (params) => {
											const { row } = params;

											return (
												<Status
													status={row.status}
													clickable
													correlationId={row.id}
													onClick={isContractor ? handleOpenStatusEditingDialog : undefined}
												/>
											);
										},
									},
									{
										field: 'actions',
										headerName: 'Actions',
										headerAlign: 'center',
										align: 'right',
										flex: 0.4,
										sortable: false,
										filterable: false,
										resizable: false,
										renderCell: (params) => {
											const {
												row: { id },
											} = params;

											return (
												<IconButton size="large" color="inherit" data-request-id={id} onClick={handleOpenMenu}>
													<Iconify icon={'eva:more-vertical-fill'} />
												</IconButton>
											);
										},
									},
								]}
								rows={requests.map((request) => {
									return {
										id: request.id,
										testingLocation: request.location,
										assessmentDates: request.assessmentDates,
										company: request.user?.company,
										paymentStatus: request.paymentStatus,
										status: request.status,
										tests: request.tests,
									} as DataGridRow;
								})}
							/>
						</DataGridContainer>

						{/* request menu options popover */}
						<Popover
							open={Boolean(anchorEl)}
							onClose={handleCloseMenu}
							anchorEl={anchorEl}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							PaperProps={{
								sx: {
									p: 1,
									width: 140,
									'& .MuiMenuItem-root': {
										px: 1,
										typography: 'body2',
										borderRadius: 0.75,
									},
								},
							}}
						>
							<MenuItem onClick={handleOpenRequestDetail}>
								<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
								View
							</MenuItem>

							{!isContractor && !BulkRequest.isCancelled(selectedRequest?.status) && (
								<MenuItem onClick={handleEditRequest}>
									<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
									Edit
								</MenuItem>
							)}

							{!isContractor && (
								<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteRequest}>
									<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
									Delete
								</MenuItem>
							)}
						</Popover>

						{/* status editing (CONTRACTOR only) */}
						<Dialog
							open={Boolean(statusEditingDialogOpen)}
							onClose={handleCloseStatusEditingDialog}
							title={`Change request ${selectedRequest?.id} status`}
						>
							<Grid container spacing={2} sx={{ m: 2 }}>
								<Grid size={{ md: 12 }}>
									<NativeSelect
										defaultValue={selectedRequest?.status}
										inputProps={{
											name: 'status',
											id: 'uncontrolled-native',
											onChange: onStatusChange,
										}}
									>
										{['--- Please Select ---', ...statusOptions].map((status, index) => (
											<option key={status} value={status}>
												{status}
											</option>
										))}
									</NativeSelect>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button
										loading={isUpdateReqStatusLoading}
										variant="outlined"
										color="primary"
										onClick={onStatusUpdate}
									>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button color="error" variant="outlined" onClick={handleCloseStatusEditingDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>
					</>
				)}
			</Box>
		</>
	);
}
